// composite types

import {chat} from "@/lib/proto";
import IAvatar = chat.IAvatar;

export interface ServerInfo {
    version: string;
}

export interface Doc {
    text: string;
}

export interface EventInviteCandidates {
    users: UserAvatar[];
    groups: GroupAvatar[];
}

export interface EventFollowRequest {
    follow?: boolean,
    attending?: boolean,
    not_attending?: boolean,
}

// auth types

export interface Session {
    session_id: string;
    user_id: number;
    username: string;
}

export interface MyAccount {
    avatar: UserAvatar;
    avatar_id: string | null;
    avatar_original_url: string | null;
    email: string | null;
    timezone: string;
    pending_emails: string[];
    friend_ids: number[];
}

export type Activity =
    "unspecified" |
    "cancelled" |
    "invited" |
    "updated" |
    "temporal" |
    "location" |
    "permissions" |
    "follows" |
    "attends" |
    "solicits";

export interface ActivityRef {
    event_id: string | null,
    group_id: string | null,
}

export interface AggregateActivity {
    reference: ActivityRef;
    event: EventHappening | null;
    group: Group | null;
    followers: UserAvatar[];
    attendees: UserAvatar[];
    solicitors: UserAvatar[];
    activities: Activity[];
    activity_at: string;
}

export interface ServerSideNotification {
    "eventlista.url": string;
}

export function aggregateActivityKey(a: AggregateActivity) {
    if (a.reference.event_id) {
        return 'event:' + a.reference.event_id;
    } else if (a.reference.group_id) {
        return 'group:' + a.reference.group_id;
    } else {
        return '';
    }
}

export interface BackendError extends Error {
    status: number,
}

export class LoginRequest {
    username!: string;
    password!: string;
}

export interface CreateUserRequest {
    username: string;
    password: string;
    email: string;
    timezone: string;
}

export class PasswordResetRequest {
    username?: string;
    password?: string;
    password_reset_id?: string;
    password_reset_token?: string;
}

export interface FCMTokenRegisterRequest {
    token: string;
}

export interface FCMTokenUnregisterRequest {
    id: string;
}

// profile types

export class SetThumbnailRequest {
    thumbnail_id?: string;
}

export class AccountEditRequest {
    color?: string;
    thumbnail_id?: string;
    gecos?: string;
    email?: string;
    password?: string;
    email_verification_code?: string;
}

export interface ProfileFollowRequest {
    follow?: boolean,
}

export interface UserAvatar {
    id: number;
    username: string;
    gecos: string;
    color: string;
    contrast_color: string;
    avatar_url: string | null;
}

export function UserAvatarFromProto(proto: IAvatar): UserAvatar {
    return {
        id: proto.id!,
        username: proto.username!,
        color: proto.color!,
        contrast_color: proto.contrastColor!,
        gecos: proto.gecos!,
        avatar_url: proto.avatarUrl || null,
    }
}

export interface UserProfile {
    avatar: UserAvatar;
    is_following: boolean;
    is_followed_by: boolean;
    friends: UserAvatar[];
    followers: UserAvatar[];
    common_friends: UserAvatar[];
    potential_relationships: UserAvatar[];
}

// group types

export interface GroupAvatar {
    id: string;
    name: string;
    color: string;
    contrast_color: string;
    avatar_url: string | null;
}

// picture types

export enum ThumbnailSize {
    CARD = "card",
    AVATAR = "avatar",
}

export class CropRequest {
    picture_id!: string;
    x!: number;
    y!: number;
    scale!: number;
    size_type!: ThumbnailSize;
}

export class CropResponse {
    thumbnail_id!: string;
    image_url!: string;
}

export class UploadResponse {
    thumbnail_id!: string;
    picture_id!: string;
    image_url!: string;
}

// event types

export enum Participating {
    HOST = "host",
    MODERATOR = "moderator",
    ATTENDING = "attending",
    FOLLOWING = "following",
    INVITED = "invited",
    NOTATTENDING = "not_attending",
    NOTINVITED = "not_invited",
    SOLICITOR = "solicitor",
    BLOCKED = "blocked",
}

export class EditableEvent {
    event!: EventHappening;
    picture_id!: string | null;
    original_image_url!: string;
}

export class EditEventRequest {
    publish?: boolean;
    notify?: boolean;
    title?: string;
    color?: string;
    location?: string | null;
    info?: string | null;
    start_date?: string;
    start_time?: string;
    end_date?: string | null;
    end_time?: string | null;
    thumbnail_id?: string;
    access_level?: AccessLevel;
    open_posting?: boolean;
    open_invite?: boolean;
}

export interface EventRoles {
    is_host: boolean;
    is_moderator: boolean;
    is_blocked: boolean;
    can_post: boolean;
    can_invite: boolean;
}

export interface BlockParticipantRequest {
    user_id: number;
}

export interface EditParticipantRequest {
    user_id: number;
    roles: EventRoles;
}

export class EventInviteRequest {
    user_ids: number[];
    group_ids: string[];

    constructor(user_ids: number[], group_ids: string[]) {
        this.user_ids = user_ids;
        this.group_ids = group_ids;
    }
}

export interface EventHappening {
    event_id: string;
    url: string;
    title: string;
    start: string;
    end: string | null;
    image_url: string | null;
    location: string | null;
    info: string | null;
    color: string;
    contrast_color: string;
    cancelled: boolean;
    open_posting: boolean;
    open_invite: boolean;
    is_following: boolean;
    invited_by: UserAvatar | null;
    participation_status: Participating;
    access_level: AccessLevel;
    permissions: EventPermissions;
    participants: EventParticipant[];
}

export interface EventPermissions {
    edit: boolean;
    moderate: boolean;
    invite: boolean;
    participate: boolean;
    delete: boolean;
    discuss: boolean;
}

export type AccessLevel = "public" | "private" | "secret";

export interface EventParticipant {
    avatar: UserAvatar;
    status: Participating;
    has_relation: boolean;
    can_post: boolean;
    can_invite: boolean;
}

export type SearchFilter = "any" | "owned";

export class SearchRequest {
    query?: string;
    filter?: SearchFilter;

    constructor(query: string | undefined, filter: string | undefined) {
        this.query = query;
        this.filter = filter as SearchFilter;
    }

    urlParams() {
        let parms = new URLSearchParams();
        if (!!this.query) {
            parms.set("query", this.query);
        }
        if (!!this.filter) {
            parms.set("filter", this.filter);
        }
        return parms;
    }
}

/* group types */

export enum Membership {
    ADMINISTRATOR = "administrator",
    MODERATOR = "moderator",
    MEMBER = "member",
    INVITED = "invited",
    NOTMEMBER = "not_member",
    SOLICITOR = "solicitor",
    BLOCKED = "blocked",
}

export interface Member {
    avatar: UserAvatar;
    status: Membership;
}

export interface GroupPermissions {
    edit: boolean;
    moderate: boolean;
    invite: boolean;
}

export interface Group {
    group_id: string;
    url: string;
    name: string;
    image_url: string | null;
    info: string | null;
    color: string;
    contrast_color: string;
    open_posting: boolean;
    open_invite: boolean;
    invited_by: UserAvatar | null;
    membership_status: Membership;
    public: boolean;
    permissions: GroupPermissions;
    members: Member[];
}

export class EditGroupRequest {
    publish?: boolean;
    notify?: boolean;
    name?: string;
    info?: string | null;
    color?: string;
    thumbnail_id?: string;
    public?: boolean;
    open_posting?: boolean;
    open_invite?: boolean;
}

export interface EditableGroup {
    group: Group;
    original_banner_url: string | null;
    banner_picture_id: string | null;
    original_avatar_url: string | null;
    avatar_picture_id: string | null;
}

export interface GroupFollowRequest {
    want_membership?: boolean,
}

export interface GroupRoles {
    is_admin: boolean;
    is_moderator: boolean;
    is_blocked: boolean;
    can_post: boolean;
    can_invite: boolean;
}

export interface BlockMembershipRequest {
    user_id: number;
}

export interface EditMembershipRequest {
    user_id: number;
    roles: GroupRoles;
}

export class GroupInviteRequest {
    user_ids: number[];

    constructor(user_ids: number[]) {
        this.user_ids = user_ids;
    }
}

export interface GroupInviteCandidates {
    users: UserAvatar[];
}

// Chat types

export interface ChatPreview {
    participants: UserAvatar[],
    num_replies: number,
    num_truncated: number,
    num_thumbnails: number,
    priority: number,
    first_message: ChatMessage,
    final_messages: ChatMessage[],
}

export interface ChatMessage {
    id: string;
    sender: UserAvatar;
    sent_at: Date;
    received_at: Date;
    text: string;
    thumbnail_ids: string[];
}

export interface ListChatsResponse {
    chat_ids: string[];
}

// request and response
export interface ChatPreferences {
    subscribed: boolean,
    muted: boolean,
    hidden: boolean,
}

// request and response
export interface ChatSettings {
    title: string,
    pinned: boolean,
    locked: boolean,
}